import { useBrowserBackDismiss } from "@/hooks/use-browser-back-dismiss";
import { isAxiosError } from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { hasHttpStatus } from "@/lib/api-error";
import {
  useCenterIntakeObligations,
  useReceiveCenterIntakeObligationBatch,
  useReportIntakeObligationDiscrepancy,
  type BatchReceiptInput,
  type IntakeObligationDiscrepancyInput,
} from "@/api/center-intake-obligations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { useOptionalDispatchCenterScope } from "@/services/dispatch-center-scope";
import type { CenterIntakeObligationRow } from "@/types/center-intake-obligation";

export interface ReturningFilter {
  centerId?: string;
  operatorId?: string;
}
export function CenterIntakeObligationsSection({
  filter,
  onRefresh,
}: {
  filter?: ReturningFilter;
  onRefresh?: () => Promise<unknown>;
}) {
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  return (
    <ReceivingSection
      key={`${dispatchCenterId}:${filter?.centerId}:${filter?.operatorId}`}
      filter={filter}
      onRefresh={onRefresh}
      dispatchCenterId={dispatchCenterId}
    />
  );
}
function ReceivingSection({
  dispatchCenterId,
  filter,
  onRefresh,
}: {
  filter?: ReturningFilter;
  onRefresh?: () => Promise<unknown>;
  dispatchCenterId: string | null;
}) {
  const client = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"outstanding" | "received" | "history">(
    "outstanding"
  );
  const query = useCenterIntakeObligations(
    page,
    search,
    (filter?.centerId || filter?.operatorId) && view === "outstanding"
      ? "returning"
      : view,
    filter
  );
  const receive = useReceiveCenterIntakeObligationBatch();
  const report = useReportIntakeObligationDiscrepancy();
  const [selected, setSelected] = useState<CenterIntakeObligationRow[]>([]);
  const [review, setReview] = useState(false);
  const [detail, setDetail] = useState<CenterIntakeObligationRow | null>(null);
  const receiptSubmitLock = useRef(false);
  const [receiptRefreshRequired, setReceiptRefreshRequired] = useState(false);
  const [receiptFeedback, setReceiptFeedback] = useState("");
  const [noteAttempt, setNoteAttempt] =
    useState<IntakeObligationDiscrepancyInput | null>(null);
  const [condition, setCondition] = useState<"MISSING" | "DAMAGED">("MISSING");
  const [note, setNote] = useState("");
  const [feedback, setFeedback] = useState("");
  const [denialStamp, setDenialStamp] = useState<number | null>(null);
  const deniedRead =
    query.isError &&
    [401, 403, 404].some((status) => hasHttpStatus(query.error, status));
  const authorizedRead =
    query.isSuccess && !query.isFetching && query.dataUpdatedAt !== denialStamp;
  const conceal = deniedRead || (denialStamp !== null && !authorizedRead);
  // An offline retry after denial cannot authorize old cached facts. Keep private
  // note replay keys and drafts internally until a fresh read restores access.
  useEffect(() => {
    if (deniedRead && denialStamp === null) {
      setDenialStamp(query.dataUpdatedAt);
      // Remove all denied-scope pages/views so a Tasks tab unmount cannot later
      // re-expose old facts when an offline error replaces the denial.
      const prefix = ["carrier", "center-intake-obligations"];
      if (dispatchCenterId === null) client.removeQueries({ queryKey: prefix });
      else {
        client.removeQueries({ queryKey: [...prefix, dispatchCenterId] });
        // All-centers pages overlap the denied facts; disjoint centers do not.
        client.removeQueries({ queryKey: [...prefix, null] });
      }
    } else if (denialStamp !== null && authorizedRead) setDenialStamp(null);
  }, [
    deniedRead,
    query.dataUpdatedAt,
    denialStamp,
    authorizedRead,
    client,
    dispatchCenterId,
  ]);
  const stale = conceal || query.isError || query.isFetching || !query.data;
  const busy = receive.isPending || report.isPending;
  useBrowserBackDismiss({
    open: review && !conceal,
    canDismiss: !busy,
    onDismiss: () => setReview(false),
  });
  useBrowserBackDismiss({
    open: !!detail && !conceal,
    canDismiss: !busy && !noteAttempt,
    onDismiss: () => {
      setDetail(null);
      setFeedback("");
    },
  });
  const rows = query.data?.data ?? [];
  const currentDetail = rows.find((r) => r.id === detail?.id) ?? detail;
  const centerId = selected[0]?.destinationCenterId;
  const changed = selected.some((s) => {
    const current = rows.find((r) => r.id === s.id);
    return (
      current &&
      (current.status !== "OPEN" ||
        current.version !== s.version ||
        current.packageIds.join("\0") !== s.packageIds.join("\0"))
    );
  });
  const packageCount = selected.reduce((n, r) => n + r.packageCount, 0);
  const groups = new Map<string, CenterIntakeObligationRow[]>();
  for (const row of rows) {
    const operatorId =
      row.status === "CLOSED"
        ? row.latestConfirmedOperatorId
        : row.custodianOperatorId;
    const key = `${row.destinationCenterId ?? "unknown"}:${row.status}:${operatorId ?? "unknown"}`;
    groups.set(key, [...(groups.get(key) ?? []), row]);
  }
  const toggle = (row: CenterIntakeObligationRow) => {
    if (receiptRefreshRequired || busy || conceal) return;
    setReceiptFeedback("");
    setSelected((old) =>
      old.some((r) => r.id === row.id)
        ? old.filter((r) => r.id !== row.id)
        : [...old, row]
    );
  };
  const refreshGoods = async () => {
    const refreshed = await query.refetch();
    await onRefresh?.();
    if (view === "outstanding" && refreshed.isSuccess) {
      setReceiptRefreshRequired(false);
      setReceiptFeedback(
        "Outstanding goods refreshed. Reselect goods for a new confirmation."
      );
    }
  };
  const confirm = async () => {
    if (
      conceal ||
      busy ||
      receiptSubmitLock.current ||
      !centerId ||
      stale ||
      changed ||
      receiptRefreshRequired
    )
      return;
    const input: BatchReceiptInput = {
      centerId,
      items: selected.map((r) => ({
        id: r.id,
        expectedVersion: r.version,
        packageIds: r.packageIds,
      })),
    };
    receiptSubmitLock.current = true;
    try {
      const result = await receive.mutateAsync(input);
      setReceiptFeedback(
        result.receipts.some((r) => r.timing === "FAILED_OR_CONFLICT")
          ? "Receipt saved. Delivery timing needs review."
          : "Receipt saved."
      );
      setSelected([]);
      setReview(false);
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      setReceiptFeedback(
        status === 401 || status === 403
          ? "Center access denied. The receipt is unconfirmed; it may have saved. Restore access and refresh outstanding goods before selecting a new receipt."
          : status === 409
            ? "Receipt selection conflicts with current goods. An earlier attempt may have saved or may still be processing. Refresh outstanding goods, then reselect and make a new confirmation."
            : status === 400
              ? "Receipt rejected. Check received history and refresh outstanding goods before making a new selection."
              : "Receipt unconfirmed; it may have saved or may still be processing. Refresh outstanding goods, then reselect and make a new confirmation."
      );
      setReceiptRefreshRequired(true);
      setSelected([]);
      setReview(false);
    } finally {
      receiptSubmitLock.current = false;
    }
  };
  const saveNote = async () => {
    if (conceal || !currentDetail?.destinationCenterId || busy || !note.trim())
      return;
    const input = noteAttempt ?? {
      id: currentDetail.id,
      centerId: currentDetail.destinationCenterId,
      requestId: crypto.randomUUID(),
      condition,
      note: note.trim(),
    };
    setNoteAttempt(input);
    try {
      await report.mutateAsync(input);
      setNoteAttempt(null);
      setNote("");
      setFeedback(
        "Discrepancy saved. Custody and outstanding work are unchanged."
      );
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      setFeedback(
        status === 401 || status === 403
          ? "Center access denied. Your note request is retained; an earlier unconfirmed attempt may already have saved. Sign in or restore center access before retrying."
          : status === 409
            ? "Note request conflicts with saved history. Refresh history before adding another note. The prior request is retained; an earlier unconfirmed attempt may already have saved."
            : status === 400
              ? "Note request rejected. Review the note before retrying. The prior request is retained; check history if an earlier attempt was unconfirmed."
              : "Note unconfirmed; retry the same note to recover. No receipt was requested."
      );
    }
  };
  if (conceal)
    return (
      <section className="space-y-2" aria-label="Returning to center">
        <h3 className="text-sm font-semibold">Returning to center</h3>
        <p role="alert" className="text-xs text-destructive">
          Center access is unavailable. Cached goods and actions are hidden
          until an authorized refresh succeeds. Your drafts are retained
          privately.
        </p>
        <Button
          size="sm-touch"
          variant="outline"
          disabled={query.isFetching || busy}
          onClick={() => void refreshGoods()}
        >
          Refresh returns
        </Button>
      </section>
    );
  return (
    <section className="space-y-2" aria-label="Returning to center">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Returning to center</h3>
        <Button
          size="sm-touch"
          variant="outline"
          disabled={query.isFetching || busy}
          onClick={() => void refreshGoods()}
        >
          Refresh returns
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {view === "outstanding"
          ? "All outstanding handoffs, across route dates and completed Trips."
          : view === "received"
            ? "Saved physical receipts for the authorized center scope."
            : "Saved receipts and ended center duties for the authorized center scope."}{" "}
        Last confirmed custody is not live location.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm-touch"
          variant={view === "outstanding" ? "default" : "outline"}
          disabled={busy}
          onClick={() => {
            setView("outstanding");
            setPage(1);
          }}
        >
          Outstanding
        </Button>
        <Button
          size="sm-touch"
          variant={view === "received" ? "default" : "outline"}
          disabled={busy}
          onClick={() => {
            setView("received");
            setPage(1);
          }}
        >
          Receipt history
        </Button>
        <Button
          size="sm-touch"
          variant={view === "history" ? "default" : "outline"}
          disabled={busy}
          onClick={() => {
            setView("history");
            setPage(1);
          }}
        >
          Resolved history
        </Button>
      </div>
      <Input
        aria-label="Search returning Shipment number"
        placeholder="Shipment number"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />
      {receiptFeedback && !review && !detail ? (
        <p
          role={receiptRefreshRequired ? "alert" : "status"}
          className="text-xs"
        >
          {receiptFeedback}
        </p>
      ) : null}
      {query.isError ? (
        <p role="alert" className="text-xs text-destructive">
          {view === "history"
            ? "Couldn't refresh resolved history. Refresh to try again."
            : "Couldn't refresh returning goods. Receipt is unavailable until refreshed."}
        </p>
      ) : null}
      {query.isLoading ? (
        <p className="text-xs">Loading returning goods…</p>
      ) : !query.isError && !rows.length ? (
        <p className="text-xs">
          {view === "history" ? "No resolved handoffs." : "No returning goods."}
        </p>
      ) : null}
      {[...groups].map(([key, goods]) => (
        <div className="space-y-1" key={key}>
          <h4 className="text-xs font-semibold">
            {goods[0].destinationCenterName ?? "Destination unknown"} ·{" "}
            {goods[0].status === "RECEIVED"
              ? "Received from "
              : goods[0].status === "CLOSED"
                ? "Center duty ended · Latest confirmed operator: "
                : ""}
            {(goods[0].status === "RECEIVED"
              ? goods[0].lastConfirmedCustodian
              : latestOperatorName(goods[0])) ?? "Unknown holder"}
          </h4>
          {goods.map((row) => (
            <article
              key={row.id}
              className="space-y-1 rounded-md border bg-background p-3 text-xs"
            >
              <div className="flex items-center gap-2">
                {row.status === "OPEN" ? (
                  <div className="flex min-h-11 min-w-11 items-center justify-center md:min-h-9 md:min-w-9">
                    <Checkbox
                      className="h-11 w-11 md:h-4 md:w-4"
                      aria-label={`Select SHP-${row.shipmentNumber}`}
                      checked={selected.some((r) => r.id === row.id)}
                      disabled={
                        stale ||
                        busy ||
                        receiptRefreshRequired ||
                        (!!centerId && centerId !== row.destinationCenterId) ||
                        (selected.length >= 100 &&
                          !selected.some((r) => r.id === row.id))
                      }
                      onCheckedChange={() => toggle(row)}
                    />
                  </div>
                ) : null}
                <Button
                  size="sm-touch"
                  variant="ghost"
                  className="h-auto min-h-11 whitespace-normal text-left md:min-h-9"
                  onClick={() => {
                    setDetail(row);
                    setNote("");
                    setNoteAttempt(null);
                    setFeedback("");
                  }}
                >
                  SHP-{row.shipmentNumber} · {row.packageCount}{" "}
                  {row.packageCount === 1 ? "package" : "packages"}
                </Button>
              </div>
              <p>
                {row.reason ?? "Center handoff"} · Source Task #
                {row.sourceTaskNumber}
              </p>
              {row.status === "CLOSED" ? <p>Center duty ended</p> : null}
              {row.custodyDisputed ? (
                <p>
                  Collection discrepancy unresolved; last confirmation retained.
                </p>
              ) : null}
              {row.status === "BLOCKED" ? (
                <p>
                  Needs investigation:{" "}
                  {row.blockReason?.toLowerCase().replace(/_/g, " ")}. Owner:{" "}
                  {row.ownerCarrierName}
                  {row.ownerCenterName ? ` / ${row.ownerCenterName}` : ""}.
                  Source Task #{row.sourceTaskNumber}.
                </p>
              ) : null}
              {row.discrepancies?.map((d) => (
                <p key={d.id}>
                  {d.condition === "MISSING"
                    ? row.status === "RECEIVED" || row.status === "CLOSED"
                      ? "Missing reported"
                      : "Missing — outstanding"
                    : "Damage reported"}
                  : {d.note} · {d.actor.name} ·{" "}
                  {new Date(d.occurredAt).toLocaleString()}
                </p>
              ))}
            </article>
          ))}
        </div>
      ))}
      {page > 1 || (query.data?.total ?? 0) > 20 ? (
        <div className="flex items-center justify-between gap-2">
          <Button
            size="sm-touch"
            disabled={page === 1 || query.isFetching || busy}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous returns
          </Button>
          <span className="text-xs">Page {page}</span>
          <Button
            size="sm-touch"
            disabled={stale || busy || page * 20 >= (query.data?.total ?? 0)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next returns
          </Button>
        </div>
      ) : null}
      {selected.length ? (
        <div className="sticky bottom-0 space-y-2 border-t bg-background p-2">
          <p className="text-xs">
            Selected: {selected.length} Shipments · {packageCount} packages at{" "}
            {selected[0].destinationCenterName}. Selection includes other
            pages/searches.
          </p>
          <Button
            size="touch"
            disabled={busy || stale || receiptRefreshRequired}
            onClick={() => setReview(true)}
          >
            Review selected receipt
          </Button>
        </div>
      ) : null}
      <Sheet
        open={review}
        onOpenChange={(open) => {
          if (!busy) setReview(open);
        }}
      >
        <SheetContent
          className="flex w-full flex-col sm:max-w-lg"
          closeDisabled={busy}
        >
          <SheetHeader>
            <SheetTitle>Confirm physical receipt</SheetTitle>
            <SheetDescription>
              {selected.length} Shipments · {packageCount} packages at{" "}
              {selected[0]?.destinationCenterName}. Receive every package of
              each selected Shipment. Confirm only goods physically present.
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
            {selected.map((row) => (
              <div key={row.id} className="rounded-md border p-3 text-sm">
                SHP-{row.shipmentNumber} · {row.packageCount} packages ·{" "}
                {row.lastConfirmedCustodian ?? "Unknown holder"}
                <Button
                  size="sm-touch"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => toggle(row)}
                >
                  Remove SHP-{row.shipmentNumber}
                </Button>
              </div>
            ))}
            {changed || stale ? (
              <p role="alert">
                Information changed or is refreshing. Refresh and reselect
                changed goods before a new receipt.
              </p>
            ) : null}
            {receiptFeedback ? <p role="alert">{receiptFeedback}</p> : null}
            <Button
              variant="outline"
              size="touch"
              disabled={busy || query.isFetching}
              onClick={() => void refreshGoods()}
            >
              Refresh receipt details
            </Button>
          </div>
          <SheetFooter>
            <Button
              size="touch"
              variant="outline"
              disabled={busy}
              onClick={() => setReview(false)}
            >
              Back
            </Button>
            <Button
              size="touch"
              disabled={
                busy ||
                !selected.length ||
                stale ||
                changed ||
                receiptRefreshRequired
              }
              onClick={() => void confirm()}
            >
              {receive.isPending ? "Saving…" : "Confirm all packages received"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      <Sheet
        open={!!detail}
        onOpenChange={(open) => {
          if (!open && !busy && !noteAttempt) {
            setDetail(null);
            setFeedback("");
          }
        }}
      >
        <SheetContent
          className="flex w-full flex-col sm:max-w-lg"
          closeDisabled={busy || !!noteAttempt}
        >
          <SheetHeader>
            <SheetTitle>SHP-{currentDetail?.shipmentNumber}</SheetTitle>
            <SheetDescription>
              {currentDetail?.packageCount} packages ·{" "}
              {currentDetail?.destinationCenterName ?? "Destination unknown"}.
              Whole Shipment custody.
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto text-sm">
            <p>
              {currentDetail?.status === "RECEIVED"
                ? "Prior operator before receipt"
                : currentDetail?.status === "CLOSED"
                  ? "Latest confirmed operator"
                  : "Last confirmed holder"}
              :{" "}
              {(currentDetail?.status === "RECEIVED"
                ? currentDetail.lastConfirmedCustodian
                : currentDetail
                  ? latestOperatorName(currentDetail)
                  : null) ?? "Unknown"}
            </p>
            {currentDetail?.status === "RECEIVED" &&
            currentDetail.latestConfirmedOperatorName !== undefined ? (
              <p>
                Latest confirmed operator:{" "}
                {currentDetail.latestConfirmedOperatorName ?? "Unknown"}
              </p>
            ) : null}
            <p>
              Source Task #{currentDetail?.sourceTaskNumber} ·{" "}
              {currentDetail?.reason ?? "Center handoff"}
            </p>
            <p>
              {currentDetail?.status === "CLOSED"
                ? "Center duty: ended"
                : `Receipt state: ${currentDetail?.status}`}
            </p>
            {currentDetail?.packageIds.map((id) => (
              <p key={id}>Package {id}</p>
            ))}
            {currentDetail?.pendingTransfer ? (
              <p>
                Transfer pending to {currentDetail.pendingTransfer.receiverName}
                ; last confirmed holder retained.
              </p>
            ) : null}
            {currentDetail?.transferHistory?.map((t) => (
              <p key={t.version}>
                Transfer: {t.priorOperatorId} → {t.receiverOperatorId} ·{" "}
                {new Date(t.occurredAt).toLocaleString()}
              </p>
            ))}
            {currentDetail?.physicalHistory?.map((event) => (
              <div key={event.version} className="space-y-1">
                <p>
                  {event.kind === "ONWARD_COLLECTED"
                    ? "Collected for onward delivery"
                    : "Physical outcome recorded"}{" "}
                  · {event.packageIds.length} packages ·{" "}
                  {new Date(event.occurredAt).toLocaleString()}
                </p>
                <p>Receiver: {event.receiverName ?? "Unknown"}</p>
                {event.tripId ? <p>Trip {event.tripId}</p> : null}
                {event.physicalTaskId ? (
                  <p>
                    Task {event.physicalTaskId}
                    {event.physicalTaskType
                      ? ` · ${event.physicalTaskType.toLowerCase().replace(/_/g, " ")}`
                      : ""}
                  </p>
                ) : null}
              </div>
            ))}
            {currentDetail?.receiptHistory?.map((r) => (
              <p key={r.occurredAt}>
                Received {r.packageIds.length} packages · Staff{" "}
                {r.actor === undefined
                  ? (r.actorCarrierUserId ?? "Unknown actor")
                  : (r.actor?.name ?? "Unknown actor")}{" "}
                · Center {r.centerId} ·{" "}
                {new Date(r.occurredAt).toLocaleString()}
              </p>
            ))}
            {currentDetail?.discrepancies?.map((d) => (
              <p key={d.id}>
                {d.condition}: {d.note} · {d.actor.name} ·{" "}
                {new Date(d.occurredAt).toLocaleString()}
              </p>
            ))}
            {currentDetail?.destinationCenterId ? (
              <>
                <p>
                  {currentDetail.status === "RECEIVED"
                    ? "Record an observation independently. A note does not change the saved receipt or custody."
                    : currentDetail.status === "CLOSED"
                      ? "Record an independent observation in the saved history."
                      : "Report condition independently. Missing stays outstanding; physically present damaged goods may still be received."}
                </p>
                <div className="flex gap-2">
                  {(["MISSING", "DAMAGED"] as const).map((c) => (
                    <Button
                      key={c}
                      size="touch"
                      variant={condition === c ? "default" : "outline"}
                      disabled={busy || !!noteAttempt}
                      onClick={() => setCondition(c)}
                    >
                      {c === "MISSING" ? "Missing" : "Damaged"}
                    </Button>
                  ))}
                </div>
                <Textarea
                  aria-label="Discrepancy note"
                  maxLength={2000}
                  value={note}
                  disabled={busy || !!noteAttempt}
                  onChange={(e) => setNote(e.target.value)}
                />
                <Button
                  size="touch"
                  disabled={busy || !note.trim() || (stale && !noteAttempt)}
                  onClick={() => void saveNote()}
                >
                  {noteAttempt ? "Retry same note" : "Save discrepancy note"}
                </Button>
                {noteAttempt ? (
                  <Button
                    size="touch"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setNoteAttempt(null);
                      setNote("");
                      setFeedback(
                        "The earlier note may already be saved. Refresh its history before adding another."
                      );
                    }}
                  >
                    Stop retrying this note
                  </Button>
                ) : null}
              </>
            ) : null}
            {feedback ? <p role="status">{feedback}</p> : null}
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
}

function latestOperatorName(row: CenterIntakeObligationRow) {
  return row.latestConfirmedOperatorName === undefined &&
    row.status !== "CLOSED"
    ? row.lastConfirmedCustodian
    : row.latestConfirmedOperatorName;
}
